import { useState } from "react";

export type QRTemplate =
  | "classic"
  | "modern"
  | "business"
  | "social"
  | "gradient"
  | "dark"
  | "rounded"
  | "minimal"
  | "neon"
  | "card";

interface Props {
  selected: QRTemplate;
  onSelect: (template: QRTemplate) => void;
}

const templates: {
  id: QRTemplate;
  label: string;
  category: string;
  preview: React.ReactNode;
}[] = [
  {
    id: "classic",
    label: "Klasik",
    category: "minimal",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-gray-800 rounded-sm" />
      </div>
    ),
  },
  {
    id: "modern",
    label: "Modern",
    category: "business",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-slate-100 rounded-xl">
        <div className="w-8 h-8 bg-slate-800 rounded-lg" />
      </div>
    ),
  },
  {
    id: "business",
    label: "İş Dünyası",
    category: "business",
    preview: (
      <div className="w-full h-full flex flex-col items-center justify-center bg-white border border-gray-200 rounded-lg">
        <div className="w-7 h-7 border border-gray-700 rounded" />
        <div className="text-[6px] mt-1 text-gray-600 font-medium">BUSINESS</div>
      </div>
    ),
  },
  {
    id: "social",
    label: "Sosyal Medya",
    category: "social",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-blue-100 rounded-xl">
        <div className="w-8 h-8 border-2 border-pink-500 rounded-lg" />
      </div>
    ),
  },
  {
    id: "gradient",
    label: "Gradyan",
    category: "creative",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-300 to-rose-400 rounded-xl">
        <div className="w-8 h-8 bg-white/90 rounded-lg shadow-sm" />
      </div>
    ),
  },
  {
    id: "dark",
    label: "Koyu",
    category: "creative",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-gray-900 rounded-xl">
        <div className="w-8 h-8 border-2 border-white/80 rounded-lg" />
      </div>
    ),
  },
  {
    id: "rounded",
    label: "Yuvarlak",
    category: "creative",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-white rounded-2xl">
        <div className="w-10 h-10 bg-gray-800 rounded-full" />
      </div>
    ),
  },
  {
    id: "minimal",
    label: "Minimal",
    category: "minimal",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="w-6 h-6 border border-gray-400 rounded-sm" />
      </div>
    ),
  },
  {
    id: "neon",
    label: "Neon",
    category: "creative",
    preview: (
      <div className="w-full h-full flex items-center justify-center bg-gray-950 rounded-xl">
        <div className="w-8 h-8 border-2 border-cyan-400 rounded-lg shadow-[0_0_6px_rgba(34,211,238,0.6)]" />
      </div>
    ),
  },
  {
    id: "card",
    label: "Kartvizit",
    category: "business",
    preview: (
      <div className="w-full h-full flex flex-col items-center justify-center bg-white shadow-sm border border-gray-200 rounded-lg p-1">
        <div className="w-6 h-6 bg-gray-800 rounded" />
        <div className="text-[5px] mt-0.5 text-gray-400">scan me</div>
      </div>
    ),
  },
];

const categories = [
  { id: "all", label: "Tümü" },
  { id: "business", label: "İş" },
  { id: "social", label: "Sosyal" },
  { id: "creative", label: "Yaratıcı" },
  { id: "minimal", label: "Minimal" },
];

export default function TemplateSelector({ selected, onSelect }: Props) {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered =
    activeCategory === "all"
      ? templates
      : templates.filter((t) => t.category === activeCategory);

  return (
    <div className="space-y-4">
      {/* Categories */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
              activeCategory === cat.id
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {filtered.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelect(template.id)}
            className={`group flex flex-col items-center gap-2 p-2 rounded-xl border-2 transition-all cursor-pointer ${
              selected === template.id
                ? "border-blue-500 bg-blue-50/60"
                : "border-slate-100 bg-white hover:border-blue-200 hover:bg-slate-50"
            }`}
          >
            <div
              className={`w-full aspect-square rounded-lg overflow-hidden transition-transform ${
                selected === template.id ? "scale-[1.02]" : "group-hover:scale-[1.02]"
              }`}
            >
              {template.preview}
            </div>
            <span
              className={`text-xs font-medium ${
                selected === template.id ? "text-blue-700" : "text-slate-600"
              }`}
            >
              {template.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}